import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { toMoney } from '@base/utils/Formatter'
import { Utilities } from '@base/utils/Utilities'
import { UIAPI } from '@modules/src/managers/UIAPI'
import { UIGameEvent, UIEvent } from '@modules/src/enums/UIEvents'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import gsap from 'gsap'
import { Utils } from '@modules/src/utils/Utils'
import { PopupContent } from '@/modules'
import { Model } from '@/model/Model'
import { RoundRestoreUtil } from '@/utils/RoundRestoreUtil'

const FAST_PLAY_LEGACY_STYLE = {
    title: {
        fill: ['#ffc000', '#fff600', '#ffc000'],
        stroke: '#813c17',
        strokeThickness: 10,
        fontFamily: 'URWWoodTypD',
    },
    text: {
        fill: ['#ffc000', '#fff600', '#ffc000'],
        stroke: '#813c17',
        strokeThickness: 5,
        fontFamily: 'URWWoodTypD',
    },
    button: {
        fill: ['#fff600', '#fff600'],
        fontFamily: 'URWWoodTypD',
    },
}

export default class PopupManager {
    private static readonly FAST_PLAY_DEBUG = true
    private _enabled: boolean = true
    private _currentType: string
    private _isFastPlayPopupRequestPending: boolean = false
    private _isFastPlayPopupFlushScheduled: boolean = false
    private _model: Model
    private _autoRestoreConfirmTimeoutId: number = null
    private _promoFreeSpinsPopupShown: boolean = false
    private static readonly CONNECTION_ERROR_TITLE_FALLBACK = 'Message'
    private static readonly CONNECTION_ERROR_TEXT_FALLBACK = 'Server has been disconnected. Please reload the game.'
    private static readonly NO_INTERNET_TEXT_FALLBACK = 'No Active Internet Connection'
    private static readonly CONNECTION_ERROR_BUTTON_FALLBACK = 'Reload'
    private static readonly CASINO_ERROR_TITLE_FALLBACK = 'Message'
    private static readonly CASINO_ERROR_TEXT_FALLBACK = 'Server has been disconnected. Please reload the game.'
    private static readonly CASINO_ERROR_BUTTON_FALLBACK = 'Reload'
    private static readonly FAST_PLAY_TITLE_FALLBACK = 'Fast Play'
    private static readonly FAST_PLAY_TEXT_FALLBACK = 'Would you like to enable Fast Play mode?'
    private static readonly FAST_PLAY_ENABLE_FALLBACK = 'YES'
    private static readonly FAST_PLAY_DISABLE_FALLBACK = 'NO'
    private static readonly INSUFFICIENT_BALANCE_CODE = 'INSUF_BALL_001'
    private static readonly PROMO_FREE_SPINS_CODES = [
        'PROMO_FREE_SPINS',
        'PROMO_FREE_SPINS_UNFINISHED',
        'PROMO_FREE_SPINS_ENDED',
        'PROMO_FREE_SPINS_MAX_WIN_REACHED',
        'PROMO_FREE_SPINS_REACHED',
    ]
    public constructor(model: Model) {
        GameSignals.enableGamePopups.connect(this.enable.bind(this))
        GameSignals.disableGamePopups.connect(this.disable.bind(this))
        GameSignals.requestSwipeFeaturePopup.connect(this.onRequestSwipeFeaturePopup)
        ;(GameSignals as any).requestFastPlayPopup?.connect(this.onRequestFastPlayPopup)
        GameSignals.sessionTimeout.connect(this.onSessionTimeout)
        GameSignals.realityCheck.connect(this.onRealityCheckPopup)
        GameSignals.realityCheck2.connect(this.onRealityCheckPopup2)
        GameSignals.casinoError.connect(this.onCasinoError)
        GameSignals.errorConnection.connect(this.onErrorServer)
        GameSignals.previousRound.connect(this.onPreviousRoundPopup)
        GameSignals.previousRoundActive.connect(this.onPreviousRoundPopupActive)
        GameSignals.hardwareAcceleration.connect(this.onHardwareAcceleration)
        GameSignals.commonError.connect(this.onCommonError)

        this._currentType = null
        this._model = model
    }

    private enable() {
        this._enabled = true
        this.scheduleFastPlayPopupFlush()
    }

    private disable() {
        this._enabled = false
        if (this._isFastPlayPopupRequestPending) {
            this._isFastPlayPopupRequestPending = false
            ;(GameSignals as any).fastPlayPopupRejected?.emit('popups-disabled')
        }
    }

    onActionButtonClick(action: string, closePopup: () => void) {
        switch (action) {
            case 'reload':
                this.onReloadGamePageClick()
                break
            case 'quit':
            case 'lobby':
                this.onQuitClick()
                break
            default:
                this.onResumeGameClick(closePopup)
                break
        }
    }

    private onRequestSwipeFeaturePopup = () => {
        if (this._currentType != null || !this._enabled) {
            return
        }

        const config =
            typeof Facade.defaultConfig?.mobileSwipeSpin === 'object' ? Facade.defaultConfig.mobileSwipeSpin : undefined
        const popupConfig = config?.popup

        this.showPopup('swipeFeature', {
            variant: 'swipe-feature',
            title: popupConfig?.title ?? 'Swipe Feature',
            text: popupConfig?.text ?? 'Swipe Down to Spin the Reels',
            buttons: [
                {
                    label: popupConfig?.enableLabel ?? 'ENABLE',
                    callback: this.onEnableSwipeFeature,
                },
                {
                    label: popupConfig?.disableLabel ?? 'RESUME GAME',
                    callback: this.onDisableSwipeFeature,
                },
            ],
        })

        GameSignals.swipeFeaturePopupShown.emit()
    }

    private onEnableSwipeFeature = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        GameSignals.enableSwipeFeature.emit(true)
        UIAPI.emit(UIEvent.clicked_play)
    }

    private onRequestFastPlayPopup = () => {
        if (!this._enabled) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] blocked: popups disabled')
            }
            ;(GameSignals as any).fastPlayPopupRejected?.emit('popups-disabled')
            return
        }

        if (this._currentType != null) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] queued: popup manager busy', { currentType: this._currentType })
            }
            this._isFastPlayPopupRequestPending = true
            this.scheduleFastPlayPopupFlush()
            return
        }

        const hasShown = this.showFastPlayPopup()
        if (!hasShown) {
            this._isFastPlayPopupRequestPending = true
            this.scheduleFastPlayPopupFlush()
        }
    }

    private showFastPlayPopup(): boolean {
        if (PopupManager.FAST_PLAY_DEBUG) {
            console.log('[FastPlayPopup] showing Fast Play popup')
        }

        this.showPopup('fastPlayFeature', {
            variant: 'fast-play-feature',
            title: this.resolveTranslation('fastPlayHeader', PopupManager.FAST_PLAY_TITLE_FALLBACK),
            text: this.resolveTranslation('fastPlayText', PopupManager.FAST_PLAY_TEXT_FALLBACK),
            //      style: FAST_PLAY_LEGACY_STYLE,
            buttons: [
                {
                    label: this.resolveTranslation('yesButton', PopupManager.FAST_PLAY_ENABLE_FALLBACK),
                    callback: this.onEnableFastPlay,
                },
                {
                    label: this.resolveTranslation('noButton', PopupManager.FAST_PLAY_DISABLE_FALLBACK),
                    callback: this.onDisableFastPlay,
                },
            ],
        })

        if (this._currentType === 'fastPlayFeature') {
            this._isFastPlayPopupRequestPending = false
            ;(GameSignals as any).fastPlayPopupDisplayed?.emit()
            return true
        } else {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] postponed: could not display now', {
                    currentType: this._currentType,
                })
            }
            return false
        }
    }

    private scheduleFastPlayPopupFlush(): void {
        if (this._isFastPlayPopupFlushScheduled) {
            return
        }

        this._isFastPlayPopupFlushScheduled = true
        setTimeout(() => {
            this._isFastPlayPopupFlushScheduled = false
            this.tryFlushFastPlayPopupRequest()
        }, 0)
    }

    private tryFlushFastPlayPopupRequest(): void {
        if (!this._isFastPlayPopupRequestPending) {
            return
        }

        if (!this._enabled) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] dropped pending request: popups disabled')
            }
            this._isFastPlayPopupRequestPending = false
            ;(GameSignals as any).fastPlayPopupRejected?.emit('popups-disabled')
            return
        }

        if (this._currentType != null) {
            return
        }

        const hasShown = this.showFastPlayPopup()
        if (!hasShown) {
            this.scheduleFastPlayPopupFlush()
        }
    }

    private onEnableFastPlay = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        GameSignals.toggleTurbo.emit(true)
    }

    private onDisableFastPlay = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
    }

    private onDisableSwipeFeature = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        GameSignals.enableSwipeFeature.emit(false)
    }

    private onErrorServer = () => {
        const fallbackText = this.isOffline()
            ? PopupManager.NO_INTERNET_TEXT_FALLBACK
            : PopupManager.CONNECTION_ERROR_TEXT_FALLBACK

        this.showPopup('errorServer', {
            title: this.resolveTranslation('connectionErrorHeader', PopupManager.CONNECTION_ERROR_TITLE_FALLBACK),
            text: this.resolveTranslation('connectionErrorText', fallbackText),
            buttons: [
                {
                    label: this.resolveTranslation('reloadButton', PopupManager.CONNECTION_ERROR_BUTTON_FALLBACK),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onCasinoError = (data: any) => {
        console.log('[PopupManager] onCasinoError', {
            status: data?.status,
            errorCode: data?.error_code ?? data?.error?.code ?? data?.code ?? data?.error?.name,
            currentType: this._currentType,
            promoPopupShown: this._promoFreeSpinsPopupShown,
        })

        if (this.isInsufficientBalanceError(data)) {
            ;(GameSignals as any).insufficientBalance.emit(data)
            return
        }

        if (this.isPromoFreeSpinsError(data)) {
            console.log('[PopupManager] promoFreeSpins candidate', {
                promoPopupShown: this._promoFreeSpinsPopupShown,
                currentType: this._currentType,
                promoCode: data?.error_code ?? data?.error?.code ?? data?.code ?? data?.error?.name,
            })

            if (this._promoFreeSpinsPopupShown) {
                console.log('[PopupManager] promoFreeSpins skipped because popup already handled')
                return
            }

            this._promoFreeSpinsPopupShown = true
            console.log('[PopupManager] promoFreeSpins emitted', {
                promoPopupShown: this._promoFreeSpinsPopupShown,
                currentType: this._currentType,
            })
            ;(GameSignals as any).promoFreeSpins.emit(data)
            return
        }

        const statusCode = Number(data?.status)
        const resolvedServerMessage = String(data?.error_message || data?.message || '').trim()
        const resolvedErrorCode = String(
            data?.error_code || data?.error?.code || data?.code || data?.error?.name || ''
        ).trim()

        let title = data?.error?.name || PopupManager.CASINO_ERROR_TITLE_FALLBACK
        let text = resolvedServerMessage

        // If backend did not provide a message, fallback to the generic disconnection text only for HTTP 500.
        if (!text) {
            text = statusCode === 500 ? PopupManager.CASINO_ERROR_TEXT_FALLBACK : resolvedErrorCode
        }

        if (!text) {
            text = PopupManager.CASINO_ERROR_TEXT_FALLBACK
        }

        if (data?.status === 804 || data?.error_code === 'SESSION_TIMEOUT') {
            title = TranslationUtils.translate('sessionTimeoutHeader')
            text = TranslationUtils.translate('sessionTimeoutText')
        }

        this.showPopup('casinoError', {
            title,
            text,
            buttons: [
                {
                    label: PopupManager.CASINO_ERROR_BUTTON_FALLBACK,
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onCommonError = () => {
        this.showPopup('commonError', {
            title: PopupManager.CASINO_ERROR_TITLE_FALLBACK,
            text: PopupManager.CASINO_ERROR_TEXT_FALLBACK,
            buttons: [
                {
                    label: PopupManager.CASINO_ERROR_BUTTON_FALLBACK,
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onSessionTimeout = () => {
        this.showPopup('sessionTimeout', {
            title: TranslationUtils.translate('sessionTimeoutHeader'),
            text: TranslationUtils.translate('sessionTimeoutText'),
            buttons: [
                {
                    label: TranslationUtils.translate('reloadButton'),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onHardwareAcceleration = () => {
        gsap.to(this, {
            duration: 0.05,
            onComplete: () => {
                this.showPopup('hardwareAcceleration', {
                    title: TranslationUtils.translate('hardwareAccHeader'),
                    text: TranslationUtils.translate('hardwareAccText'),
                    buttons: [
                        {
                            label: TranslationUtils.translate('backButton'),
                            callback: this.onQuitClick,
                        },
                        {
                            label: TranslationUtils.translate('reloadButton'),
                            callback: this.onReloadGamePageClick,
                        },
                    ],
                })
            },
        })
    }

    private onPreviousRoundPopup = () => {
        this.showPopup('previousRound', {
            title: TranslationUtils.translate('connectionErrorHeader'),
            text: this.getPreviousRoundText(),
            buttons: [
                {
                    label: TranslationUtils.translate('okButton'),
                    callback: this.onResumeGameClick,
                },
            ],
        })
    }

    private onPreviousRoundPopupActive = () => {
        this.showPopup('previousRound', {
            title: TranslationUtils.translate('previousRoundHeader'),
            text: TranslationUtils.translate('previousRoundText'),
            buttons: [
                {
                    label: TranslationUtils.translate('okButton'),
                    callback: this.onResumeGameClick,
                },
            ],
        })
    }

    private onRealityCheckPopup = (data: any) => {
        if (data.message == null || data.message === '' || data.message === '.') {
            // manually switching to common error, when empty message provided
            this.onCommonError()
            return
        }

        let buttons = [
            {
                label: TranslationUtils.translate('reloadButton'),
                callback: this.onReloadGamePageClick,
            },
        ]

        if (data.buttons != null) {
            buttons = data.buttons.map((button: any) => {
                return {
                    label: button.text,
                    callback: this.onActionButtonClick.bind(this, button.action),
                }
            })
        }

        this.showPopup('911', {
            title: data.title,
            text: data.message,
            buttons,
        })
    }

    private onRealityCheckPopup2 = () => {
        this.showPopup('realityCheck', {
            title: TranslationUtils.translate('realityCheckHeader'),
            text: this.getRealityCheckText(),
            buttons: [
                {
                    label: TranslationUtils.translate('quitButton'),
                    callback: this.onQuitClick,
                },
                {
                    label: TranslationUtils.translate('historyButton'),
                    callback: (closePopup: () => void) => {
                        this.hidePopup()
                        closePopup()
                        window.open('', '_blank')
                    },
                },
                {
                    label: TranslationUtils.translate('keepPlayButton'),
                    callback: this.onResumeGameClick,
                },
            ],
        })
    }

    private onResumeGameClick = (closePopup: () => void) => {
        if (this._currentType === 'previousRound') {
            const okButton = TranslationUtils.translate('okButton')
            GameSignals.buttonClicked.emit(`ui-dialog-btn_${okButton}`)
        }

        this._clearAutoRestoreConfirm()
        this.hidePopup()
        closePopup()
    }

    public resetPromoFreeSpinsPopupShown() {
        this._promoFreeSpinsPopupShown = false
    }

    private onReloadGamePageClick = () => {
        location.reload()
    }

    private onQuitClick = () => {
        Utils.goToLobby()
    }

    private getRealityCheckText(): string {
        const realityCheckData = this._model.realityCheckData

        const totalSessionTime = Date.now() - realityCheckData.startSessionTime
        const hours = Math.floor(totalSessionTime / (1000 * 60 * 60))
        const minutes = Math.ceil(totalSessionTime / (1000 * 60)) - hours * 60

        return TranslationUtils.translate('realityCheckText')
            .replace('%h', hours.toString())
            .replace('%m', minutes.toString())
            .replace('%b', toMoney(realityCheckData.totalSessionBet, 2))
            .replace('%w', toMoney(realityCheckData.totalSessionWin, 2))
    }

    private getPreviousRoundText(): string {
        return ''
    }

    private resolveTranslation(key: string, fallback: string): string {
        const translated = TranslationUtils.translate(key)

        if (translated == null || translated.trim() === '' || translated === key) {
            return fallback
        }

        return translated
    }

    private isOffline(): boolean {
        return typeof navigator !== 'undefined' && navigator.onLine === false
    }

    private isInsufficientBalanceError(data: any): boolean {
        const codes = [data?.error_code, data?.error?.code, data?.code, data?.error?.name]
            .filter((value) => value != null)
            .map((value) => String(value).toUpperCase())

        if (codes.includes(PopupManager.INSUFFICIENT_BALANCE_CODE)) {
            return true
        }

        const message = String(data?.message || data?.error_message || '').toLowerCase()

        return message.includes('insufficient balance') || message.includes('insufficient funds')
    }

    private isPromoFreeSpinsError(data: any): boolean {
        const codes = [data?.error_code, data?.error?.code, data?.code, data?.error?.name]
            .filter((value) => value != null)
            .map((value) => String(value).toUpperCase())

        const promoCodes = PopupManager.PROMO_FREE_SPINS_CODES || []

        return codes.some((code) => promoCodes.includes(code))
    }

    private showPopup(type: string, content: PopupContent) {
        if (this._currentType != null || !this._enabled) {
            return
        }

        Facade.isPopupBlockingInput = true
        UIAPI.emit(UIGameEvent.show_dialog, content)
        this._currentType = type

        if (type !== 'previousRound') {
            if (this._model.spinData && this._model.spinData.freebet && !this._model.spinData.freebet.amount_left) {
                RoundRestoreUtil.stopRound()
            }
            GameSignals.gamePause.emit()
        }
    }

    private hidePopup() {
        this._clearAutoRestoreConfirm()
        Facade.isPopupBlockingInput = false
        GameSignals.gameResume.emit()
        this._currentType = null
        this.scheduleFastPlayPopupFlush()
    }

    private _clearAutoRestoreConfirm(): void {
        if (this._autoRestoreConfirmTimeoutId !== null) {
            clearTimeout(this._autoRestoreConfirmTimeoutId)
            this._autoRestoreConfirmTimeoutId = null
        }
    }
}
